import { useMemo } from 'react'

import { type DefaultTheme, useTheme } from 'styled-components'

type ThemeableFunc<T, TTheme = DefaultTheme> = (props: { theme: TTheme }) => T

/**
 * Describes a value that is either a fixed value, or a function of the current
 * theme.
 *
 * The theme-type is overridable mostly for story/testing purposes. In cases
 * where this is necessary, the recommended pattern is to let outer functions
 * accept a `<TTheme = DefaultTheme>` generic parameter passed to the themable,
 * while internal components can simply use `Themeable<..., any>` to avoid type
 * problems.
 */
export type Themeable<T, TTheme = DefaultTheme> = T | ThemeableFunc<T, TTheme>

/** Resolve a themable value using the theme if necessary */
export const resolveThemeable = <T, TTheme = DefaultTheme>(themeable: Themeable<T, TTheme>, theme: TTheme): T =>
	typeof themeable === 'function' ? (themeable as ThemeableFunc<T, TTheme>)({ theme }) : themeable

/** Hook for getting the theme and resolving a themable, memoized */
export const useThemeable = <T, TTheme = DefaultTheme>(themeable: Themeable<T, TTheme>) => {
	const theme = useTheme() as TTheme

	return useMemo(() => resolveThemeable(themeable, theme), [theme, themeable])
}

/** Transform a themeable. Useful for using directly in a styled component. */
export const transformThemeable = <TBase, TReturn, TTheme = DefaultTheme>(
	themeable: Themeable<TBase, TTheme>,
	transform: (themed: TBase) => TReturn,
): Themeable<TReturn, TTheme> => {
	if (typeof themeable === 'function') {
		return ({ theme }) => transform((themeable as ThemeableFunc<TBase, TTheme>)({ theme }))
	}
	return transform(themeable)
}
